import { useId, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { usePracticeDetail } from '../../hooks/usePracticeDetail';
import { usePracticeOptions } from '../../hooks/useBilling';
import { usePageTitle } from '../../hooks/usePageTitle';
import { practiceOptionsFromList } from '../../utils/practiceFilterOptions';
import type { PracticeDetail } from '../../services/practiceDetail';
import { CommunicationFormHeader } from '../../components/accounts/practice/communications/CommunicationFormHeader';
import { CommunicationFormMain } from '../../components/accounts/practice/communications/CommunicationFormMain';
import { CommunicationFormMetaRail } from '../../components/accounts/practice/communications/CommunicationFormMetaRail';
import { DiscardChangesConfirmModal } from '../../components/accounts/practice/communications/DiscardChangesConfirmModal';
import { useCommunicationForm } from '../../components/accounts/practice/communications/useCommunicationForm';
import {
    useOpenCommunicationsCount,
    useRecentCommunications
} from '../../components/accounts/practice/communications/useRecentCommunications';

function CenteredSpinner() {
    return (
        <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
        </div>
    );
}

// Create-only: creates a thread plus its first entry. Editing happens on
// the detail page, not here.
export function CommunicationFormPage() {
    const { practiceId: routePracticeId } = useParams<{ practiceId?: string }>();
    // The only route without a practiceId param is the cross-practice
    // `/accounts/communications/new` entry.
    const practiceIsEditable = !routePracticeId;

    const [selectedPracticeId, setSelectedPracticeId] = useState<string | null>(
        routePracticeId ?? null
    );

    const {
        practice,
        isLoading: isPracticeLoading,
        isError: isPracticeError
    } = usePracticeDetail(selectedPracticeId ?? undefined);
    const { practices: practiceOptionsList, isLoading: isPracticeOptionsLoading } =
        usePracticeOptions(practiceIsEditable);

    usePageTitle(
        practiceIsEditable
            ? 'Log a communication'
            : practice
              ? `Log a communication · ${practice.name}`
              : null
    );

    // Never produced by App.tsx's routes, but guards against a stray direct
    // link to a nonexistent URL shape.
    if (!routePracticeId && !practiceIsEditable) {
        return <Navigate to="/accounts/practices" replace />;
    }

    if (!practiceIsEditable) {
        if (isPracticeLoading) {
            return <CenteredSpinner />;
        }
        if (isPracticeError || !practice) {
            return (
                <div className="flex-1 flex flex-col items-center justify-center gap-3">
                    <div className="text-sm text-red-500">Failed to load this practice</div>
                </div>
            );
        }
    }

    return (
        <CommunicationFormPageContent
            practiceId={selectedPracticeId}
            practice={practice ?? null}
            practiceIsEditable={practiceIsEditable}
            practiceOptions={practiceOptionsFromList(practiceOptionsList)}
            isPracticeOptionsLoading={isPracticeOptionsLoading}
            onPracticeChange={setSelectedPracticeId}
            defaultContact={practice?.contact?.phone ?? null}
        />
    );
}

interface CommunicationFormPageContentProps {
    practiceId: string | null;
    /** Null until a practice is loaded - always non-null when `practiceIsEditable` is false. */
    practice: PracticeDetail | null;
    /** True only on the practice-less entry point (`/accounts/communications/new`). */
    practiceIsEditable: boolean;
    practiceOptions: ReturnType<typeof practiceOptionsFromList>;
    isPracticeOptionsLoading: boolean;
    onPracticeChange: (practiceId: string) => void;
    defaultContact: string | null;
}

// Split out so useCommunicationForm (and every field's local state) only
// mounts once practice data is actually available.
function CommunicationFormPageContent({
    practiceId,
    practice,
    practiceIsEditable,
    practiceOptions,
    isPracticeOptionsLoading,
    onPracticeChange,
    defaultContact
}: CommunicationFormPageContentProps) {
    const form = useCommunicationForm({ practiceId, defaultContact, practiceIsEditable });
    const {
        lastCommunications,
        totalCommunications,
        isLoading: isRecentLoading
    } = useRecentCommunications(practiceId, undefined);
    const openCommunicationsCount = useOpenCommunicationsCount(practiceId);
    const missingBodyHintId = useId();

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <CommunicationFormHeader
                practiceId={practiceId}
                practiceName={practice?.name ?? null}
                isDirty={form.isDirty}
                isSaving={form.isSaving}
                canSave={form.canSave}
                saveDisabledReasonId={form.missingBodyText ? missingBodyHintId : undefined}
                onNavigate={form.guardedNavigate}
                onCancel={form.handleCancel}
                onSave={form.handleSave}
            />
            <div className="flex-1 min-h-0 px-8 pb-8 mt-4 overflow-hidden">
                <div className="h-full flex items-stretch gap-4">
                    <CommunicationFormMain
                        practiceId={practiceId}
                        form={form.form}
                        setForm={form.setForm}
                        onChannelChange={form.setChannel}
                        plainBodyLength={form.plainBodyLength}
                        missingBodyText={form.missingBodyText}
                        missingBodyHintId={missingBodyHintId}
                        disabled={form.isSaving}
                        durationError={form.durationError}
                        timerIsRunning={form.timerIsRunning}
                        timerElapsedLabel={form.timerElapsedLabel}
                        onToggleTimer={form.onToggleTimer}
                    />
                    <CommunicationFormMetaRail
                        practiceId={practiceId}
                        practice={practice}
                        practiceIsEditable={practiceIsEditable}
                        practiceOptions={practiceOptions}
                        isPracticeOptionsLoading={isPracticeOptionsLoading}
                        onPracticeChange={onPracticeChange}
                        disabled={form.isSaving}
                        totalCommunications={totalCommunications}
                        openCommunicationsCount={openCommunicationsCount}
                        form={form.form}
                        setForm={form.setForm}
                        lastCommunications={lastCommunications}
                        isLastCommunicationsLoading={isRecentLoading}
                        onNavigate={form.guardedNavigate}
                    />
                </div>
            </div>
            {form.showDiscardModal && (
                <DiscardChangesConfirmModal
                    onKeepEditing={form.cancelDiscardNavigation}
                    onDiscard={form.confirmDiscardNavigation}
                />
            )}
        </div>
    );
}
